import _ from 'lodash';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {VirtualTableSortableColumnDirectionsEnum} from '../../../components/common/table/helpers/tableHelpers';
import {computeAccountsSetupGuideCompletedTasks} from '../../../helpers/accountHelpers';
import {CancelError} from '../../../helpers/asyncCancelHelpers';
import {
  AccountAttributeUpdateAction,
  accountAttributeUpdateReducer,
} from '../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel, ApiAccount} from '../../../models/api/accountModel';
import {ApiAccountsSetupGuide} from '../../../models/api/accountsSetupGuideModel';
import {ApiCardsFlat, CardModel} from '../../../models/api/cardModel';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {ApiRecentConversations, RecentConversationsModel} from '../../../models/api/recentConversationsModel';
import {ApiSearchAccounts} from '../../../models/api/searchAccountsModel';
import {ApiSearchAccountsV2} from '../../../models/api/searchAccountsV2Model';
import {TeammateModel} from '../../../models/api/teammateModel';
import {
  ApiSendOptions,
  deleteAsync,
  fetchApiAsync,
  fetchAsync,
  postApiAsync,
  postAsync,
  putApiAsync,
} from '../../../services/http/http';
import {HttpError, isTransientError} from '../../../services/http/httpError';
import {findAccountLock} from '../../../services/locks/resourceLocks';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getHasAccountsSetupGuide} from '../../featureFlags/featureFlagsSelectors';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {showAccountDetailsUndoBannerAction} from '../../panels/app/banners/bannersActions';
import {FileMetadata} from '../../panels/contacts/contactsManager/file/contactsManagerFileState';
import {NewPanelId} from '../../panels/panelStateBase';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getAccounts} from '../dataSelectors';
import {DataState} from '../dataState';
import {updateAccountsSetupGuideCompletedTasksAction} from '../localPreferences/localPreferencesActions';
import {getAccountsSetupGuideCompletedTasks} from '../localPreferences/localPreferencesSelectors';
import {getHasDismissedAccountsSetupGuide} from '../preferences/preferencesSelectors';

/*
 * Actions types.
 */

export enum AccountsActionsEnum {
  ACCOUNT_DELETED = '@@data/accounts/ACCOUNT_DELETED',
  ACCOUNT_THIRD_PARTY_LOGO_FAILED = '@@data/accounts/ACCOUNT_THIRD_PARTY_LOGO_FAILED',
}

export interface AccountDeletedAction {
  type: AccountsActionsEnum.ACCOUNT_DELETED;
  payload: {
    accountId: number;
  };
}

export interface AccountThirdPartyLogoFailedAction {
  type: AccountsActionsEnum.ACCOUNT_THIRD_PARTY_LOGO_FAILED;
  payload: {
    accountId: number;
  };
}

/*
 * Action creators.
 */

export function accountDeleted(accountId: number) {
  return {
    type: AccountsActionsEnum.ACCOUNT_DELETED,
    payload: {
      accountId,
    },
  };
}

export function accountThirdPartyLogoFailed(accountId: number) {
  return {
    type: AccountsActionsEnum.ACCOUNT_THIRD_PARTY_LOGO_FAILED,
    payload: {
      accountId,
    },
  };
}

/*
 * Union type.
 */

export type AccountAction = AccountDeletedAction | AccountThirdPartyLogoFailedAction;

/*
 * Interfaces.
 */

export interface FetchAllAccountOptions extends ApiSendOptions {
  limit?: number;
  pageToken?: string;
  sortBy?: string;
  sortOrder?: VirtualTableSortableColumnDirectionsEnum;
}

export interface FetchAccountCardsOptions extends ApiSendOptions {
  limit: number;
  offset: number;
}

export interface FetchAllAccountsResponse {
  accountIds: ReadonlyArray<number>;
  next: string | undefined;
  count: number | undefined;
}

export interface FetchAccountsFullResponse {
  accounts: ReadonlyArray<AccountModel>;
  next: string | undefined;
  count: number | undefined;
}

export interface FetchAccountCardsResponse {
  cardIds: ReadonlyArray<number>;
}

export interface CreateAccountOptions {
  name: string;
  description?: string;
  domainNames?: ReadonlyArray<string>;
  customAttributes?: ReadonlyArray<CustomFieldAttribute>;
}

export interface FetchAccountsByQueryOptions extends ApiSendOptions {
  query: string;
  limit?: number;
  pageToken?: string;
}

export interface FetchAccountsByQueryV2Options extends ApiSendOptions {
  query: string;
  limit?: number;
  nextPageToken?: string;
}

export interface FetchAccountIdsByQueryResponse {
  accounts: ReadonlyArray<AccountModel>;
  teammates: ReadonlyArray<TeammateModel>;
  pageToken?: string;
}

export interface FetchAccountIdsByQueryV2Response {
  accounts: ReadonlyArray<AccountModel>;
  teammates: ReadonlyArray<TeammateModel>;
  nextPageToken?: string;
}

export interface FetchAccountsByQueryResponse {
  accounts: ReadonlyArray<AccountModel>;
  pageToken?: string;
}

export interface FetchAccountsByQueryV2Response {
  accounts: ReadonlyArray<AccountModel>;
  nextPageToken?: string;
}

enum ResponseStatusesEnum {
  OK = 'OK',
}

/*
 * Actions.
 */

export function fetchAllAccountsAsyncAction(
  options?: FetchAllAccountOptions,
): ThunkAsyncAction<FetchAllAccountsResponse> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const fetchAccountsUrl = addQueryToPath(`${company.url}/accounts`, {
      limit: options?.limit,
      page_token: options?.pageToken,
      sort_by: options?.sortBy,
      sort_order: options?.sortOrder,
    });

    try {
      const response = await fetchApiAsync(ApiSearchAccounts, fetchAccountsUrl, options);
      dispatch(updateDataAction({accounts: response.accounts, teammates: response.teammates}));
      return {
        accountIds: response.accounts.map((account) => account.id),
        next: response.next,
        count: response.count,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/**
 * This will return the full account without storing the account in the redux store.
 * Use fetchAccountsAction if you need redux stored accounts.
 */
export function fetchAccountsFullAction(
  options?: FetchAllAccountOptions,
): ThunkAsyncAction<FetchAccountsFullResponse> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;

    const fetchAccountsUrl = addQueryToPath(`${company.url}/accounts`, {
      limit: options?.limit,
      page_token: options?.pageToken,
      sort_by: options?.sortBy,
      sort_order: options?.sortOrder,
    });

    try {
      const response = await fetchApiAsync(ApiSearchAccounts, fetchAccountsUrl, options);

      return {
        accounts: response.accounts,
        next: response.next,
        count: response.count,
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountCardsAsyncAction(
  accountId: number,
  options: FetchAccountCardsOptions,
): ThunkAsyncAction<FetchAccountCardsResponse> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const fetchAccountCardsUrl = addQueryToPath(`${company.url}/accounts/${accountId}/cards`, {
      limit: options.limit,
      offset: options.offset,
    });

    try {
      const response = await fetchApiAsync(ApiCardsFlat, fetchAccountCardsUrl, options);
      dispatch(
        updateDataAction({
          cards: response.cards,
          contacts: response.contacts,
          cardGroups: response.cardGroups,
        }),
      );
      return {
        cardIds: response.cards.map((card) => card.id),
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function createAccountAsyncAction(options: CreateAccountOptions): ThunkAsyncAction<AccountModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const accountsUrl = `${company.url}/accounts`;
    const {name, description, domainNames, customAttributes} = options;
    const body = {
      name,
      description,
      domains: domainNames,
      custom_field_attributes: customAttributes?.map((attribute) => ({
        custom_field_id: attribute.id,
        value: attribute.value,
      })),
    };

    try {
      const account = await postApiAsync(ApiAccount, accountsUrl, body);
      dispatch(updateDataAction({accounts: [account]}));
      return account;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteAccountAsyncAction(accountId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const accountUrl = `${company.url}/accounts/${accountId}`;

    const account = getAccounts(getState())[accountId];
    if (!account) {
      return;
    }

    try {
      await deleteAsync(accountUrl);
      dispatch(accountDeleted(account.id));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountAsyncAction(
  accountId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<AccountModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const fetchAccountUrl = `${company.url}/accounts/${accountId}`;

    try {
      const account = await fetchApiAsync(ApiAccount, fetchAccountUrl, options);
      dispatch(updateDataAction({accounts: [account]}));
      return account;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountsAsyncAction(
  accountIds: ReadonlyArray<number>,
): ThunkAsyncAction<ReadonlyArray<AccountModel>> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const fetchRequests = accountIds.map((id) => {
      const fetchAccountUrl = `${companyUrl}/accounts/${id}`;
      return fetchApiAsync(ApiAccount, fetchAccountUrl);
    });
    try {
      const accounts = await Promise.all(fetchRequests);
      dispatch(updateDataAction({accounts}));
      return accounts;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountsForCardsAsyncAction(
  cards: ReadonlyArray<CardModel>,
): ThunkAsyncAction<ReadonlyArray<AccountModel>> {
  return async (dispatch, getState) => {
    const state = getState();
    const allAccounts = getAccounts(state);

    const accountIdsToFetch = _(cards)
      .map((card) => card.accountId)
      .compact()
      .filter((id) => !allAccounts[id]) // only get accounts not already in store
      .uniq()
      .value();

    if (accountIdsToFetch.length) {
      return await dispatch(fetchAccountsAsyncAction(accountIdsToFetch));
    }
    return [];
  };
}

export function updateAccountAttributeAsyncAction(
  account: AccountModel,
  action: AccountAttributeUpdateAction,
  panelId?: NewPanelId,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const accountUrl = `${company.url}/accounts/${account.id}`;

    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'accounts'>(
      'accounts',
      createResourceMapValueUpdater(
        account.id,
        createUpdater<AccountModel>((currentAccount) =>
          accountAttributeUpdateReducer(currentAccount, action),
        ),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findAccountLock(account.id).lockAsync(async () => {
          try {
            const reducedAccount = accountAttributeUpdateReducer(account, action);
            const payload = computePayloadForAccountAttributeUpdate(reducedAccount);
            const savedAccount = await putApiAsync(ApiAccount, accountUrl, payload);
            if (panelId) {
              dispatch(showAccountDetailsUndoBannerAction(panelId, account.id, action));
            }
            return updateDataAction({accounts: [savedAccount]});
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}

export function updateAccountAvatarAsyncAction(account: AccountModel, avatarFile: File): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const accountAvatarUrl = `${company.url}/accounts/${account.id}/avatar`;

    const formData = new FormData();
    formData.append('avatar', avatarFile);

    try {
      const updatedAccount = await putApiAsync(ApiAccount, accountAvatarUrl, formData);
      dispatch(updateDataAction({accounts: [updatedAccount]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function validateDomainAsyncAction(domain: DomainModel): ThunkAsyncAction<DomainModel> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const validateDomainUrl = addQueryToPath(`${company.url}/accounts/validate_domain`, {
      domain: domain.name,
    });

    try {
      await fetchAsync(validateDomainUrl);
      return {
        ...domain,
        validityStatus: DomainValidityStatusesEnum.VALID,
      };
    } catch (error) {
      if (error.body.status === 'bad_request') {
        return {
          ...domain,
          validityStatus: DomainValidityStatusesEnum.INVALID_COMMON,
        };
      }

      // TODO: Handle 409 response (conflict with existing account) and surface the account to the front end
      return {
        ...domain,
        validityStatus: DomainValidityStatusesEnum.INVALID_CONFLICT,
      };
    }
  };
}

export function addCardsToAccountAction(accountId: number, cardIds: ReadonlyArray<number>): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const accountsUrl = `${company.url}/accounts/${accountId}/cards`;
    const body = {
      card_ids: cardIds,
    };

    try {
      await postAsync(accountsUrl, body);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function uploadAccountsCSVAsyncActionV2(
  csvFile: File,
  dryRun: boolean = true,
  mapping?: Record<string, number>,
): ThunkAsyncAction<File | undefined> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const accountsCSVUploadUrl = addQueryToPath(`${company.url}/accounts/csv_import`, {
      execute: !dryRun,
    });

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('mapping', JSON.stringify(mapping || {}));
      const response = await postAsync(accountsCSVUploadUrl, formData);
      // if response is ok there's no warning file
      if (dryRun && response !== ResponseStatusesEnum.OK) {
        // convert the response which is a csv string to a file we can download
        return new File([new Blob([response], {type: 'text/csv'})], 'Updated CSV.csv', {
          type: 'text/csv',
        });
      }
      return undefined;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountsCsvMetadataAsyncAction(csvFile: File): ThunkAsyncAction<FileMetadata> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const accountsCSVMetadataUrl = addQueryToPath(`${company.url}/accounts/csv_import_metadata`);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await postAsync(accountsCSVMetadataUrl, formData);
      return {
        headers: res.headers,
        invalidStandardFieldCounts: res.invalid_standard_field_counts,
        invalidCustomFieldCounts: res.invalid_custom_field_counts,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function uploadAccountsCSVAsyncAction(csvFile: File, dryRun: boolean = true): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const accountsCSVUploadUrl = addQueryToPath(`${company.url}/accounts/csv`, {
      dry_run: dryRun,
    });

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      await postAsync(accountsCSVUploadUrl, formData);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateAccountAction(
  newAccount: AccountModel,
  shouldOnlyUpdateIfCurrentlyExists: boolean = false,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing account.
    const existingAccount = getState().data.accounts[newAccount.id];

    // No need to do anything if the new account is older.
    if (existingAccount && existingAccount.updatedAt > newAccount.updatedAt) {
      return null;
    }

    // Only update if we have an existing account and flag is set
    if (shouldOnlyUpdateIfCurrentlyExists && !existingAccount) {
      return null;
    }

    // Otherwise, update the account.
    return dispatch(updateDataAction({accounts: [newAccount], updatedAt: newAccount.updatedAt}));
  };
}

export function fetchConversationsForAccountIdAsyncAction(
  accountId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<RecentConversationsModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchAccountConversationsUrl = `${companyUrl}/accounts/${accountId}/conversations`;

    try {
      const accountConversations = await fetchApiAsync(
        ApiRecentConversations,
        fetchAccountConversationsUrl,
        options,
      );
      dispatch(updateDataAction({conversations: accountConversations.conversations}));
      return accountConversations;
    } catch (error) {
      // If it is transient error we can just rethrow that error as it was not contact specific
      if (isTransientError(error)) {
        throw error;
      }

      throw new ErrorWithData('Failed to fetch conversations for account', {
        id: accountId,
      });
    }
  };
}

export function fetchConversationsForAccountDomainAsyncAction(
  domain: string,
  options?: ApiSendOptions,
): ThunkAsyncAction<RecentConversationsModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchAccountConversationsUrl = `${companyUrl}/accounts/domain_conversations/${domain}`;

    try {
      const accountConversations = await fetchApiAsync(
        ApiRecentConversations,
        fetchAccountConversationsUrl,
        options,
      );
      dispatch(updateDataAction({conversations: accountConversations.conversations}));
      return accountConversations;
    } catch (error) {
      // If it is transient error we can just rethrow that error as it was not contact specific
      if (isTransientError(error)) {
        throw error;
      }

      throw new ErrorWithData('Failed to fetch conversations for account domain', {
        domain,
      });
    }
  };
}

/**
 * Fetch account ids by querying against the SQL database.
 * This should no longer be used once the Elasticsearch route is adopted.
 */
export function fetchAccountIdsByQueryAsyncAction(
  options: FetchAccountsByQueryOptions,
): ThunkAsyncAction<FetchAccountIdsByQueryResponse> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchAccountsUrl = buildSearchAccountsUrl(companyUrl, options);

    try {
      const {accounts, teammates, next} = await fetchApiAsync(ApiSearchAccounts, searchAccountsUrl, options);
      return {
        accounts,
        teammates,
        pageToken: next,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/**
 * Fetch account ids by querying against Elasticsearch.
 */
export function fetchAccountIdsByQueryV2AsyncAction(
  options: FetchAccountsByQueryV2Options,
): ThunkAsyncAction<FetchAccountIdsByQueryV2Response> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchAccountsV2Url = buildSearchAccountsV2Url(companyUrl, options);

    try {
      const {accounts, teammates, nextPageToken} = await fetchApiAsync(
        ApiSearchAccountsV2,
        searchAccountsV2Url,
        options,
      );
      return {
        accounts,
        teammates,
        nextPageToken,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountsByQueryAsyncAction(
  options: FetchAccountsByQueryOptions,
): ThunkAsyncAction<FetchAccountsByQueryResponse> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchAccountsUrl = buildSearchAccountsUrl(companyUrl, options);

    try {
      const {accounts, teammates, next} = await fetchApiAsync(ApiSearchAccounts, searchAccountsUrl, options);
      dispatch(updateDataAction({accounts, teammates}));
      return {
        accounts,
        pageToken: next,
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchAccountsByQueryV2AsyncAction(
  options: FetchAccountsByQueryV2Options,
): ThunkAsyncAction<FetchAccountsByQueryV2Response> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchAccountsV2Url = buildSearchAccountsV2Url(companyUrl, options);

    try {
      const {accounts, teammates, nextPageToken} = await fetchApiAsync(
        ApiSearchAccountsV2,
        searchAccountsV2Url,
        options,
      );
      dispatch(updateDataAction({accounts, teammates}));
      return {
        accounts,
        nextPageToken,
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchOrGetAccountAsyncAction(
  accountId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<AccountModel | undefined> {
  return async (dispatch, getState) => {
    const account = getAccounts(getState())[accountId];

    // If we found an account, return early.
    if (account) {
      return account;
    }
    // Try searching for an account via the endpoint.
    const fetchedAccount = await dispatch(fetchAccountAsyncAction(accountId, options));
    return fetchedAccount;
  };
}

export function fetchAccountsSetupGuideCompletedTasksAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const hasAccountsSetupGuide = getHasAccountsSetupGuide(getState());
    const hasDismissedAccountsSetupGuide = getHasDismissedAccountsSetupGuide(getState());

    // If feature flag is disabled or guide has been dismissed, do not fetch.
    if (!hasAccountsSetupGuide || hasDismissedAccountsSetupGuide) {
      return;
    }

    // If all tasks are already complete, no need to fetch.
    const completedTasksList = getAccountsSetupGuideCompletedTasks(getState());
    const completedTasks = computeAccountsSetupGuideCompletedTasks(completedTasksList);
    if (Object.values(completedTasks).every(Boolean)) {
      return;
    }

    const companyUrl = getCompanyUrl(getState());
    const accountsSetupGuideUrl = `${companyUrl}/accounts/accounts_setup_guide`;

    try {
      const response = await fetchApiAsync(ApiAccountsSetupGuide, accountsSetupGuideUrl);
      if (!response) {
        return;
      }

      dispatch(updateAccountsSetupGuideCompletedTasksAction(response.completedTasks));
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function fetchAccountByAliasAsyncAction(
  alias: string,
  options?: ApiSendOptions,
): ThunkAsyncAction<AccountModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const fetchAccountUrl = `${company.url}/accounts/alias/${alias}`;

    try {
      const account = await fetchApiAsync(ApiAccount, fetchAccountUrl, options);
      dispatch(updateDataAction({accounts: [account]}));
      return account;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/*
 * Helpers.
 */

function computePayloadForAccountAttributeUpdate(account: AccountModel) {
  return {
    name: account.name,
    description: account.description || null,
    domains: account.domains || [],
    custom_field_attributes: account.customFieldAttributes?.map((attribute) => ({
      custom_field_id: attribute.id,
      value: attribute.value,
    })),
  };
}

function buildSearchAccountsUrl(companyUrl: string, options: FetchAccountsByQueryOptions) {
  return addQueryToPath(`${companyUrl}/search_accounts/${encodeURIComponent(options.query.toLowerCase())}`, {
    limit: options.limit,
    page_token: options.pageToken,
  });
}

export function buildSearchAccountsV2Url(companyUrl: string, options: FetchAccountsByQueryV2Options) {
  return addQueryToPath(`${companyUrl}/search_account/${encodeURIComponent(options.query.toLowerCase())}`, {
    limit: options.limit,
    next_page_token: options.nextPageToken,
  });
}
